import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

interface Category {
  value: string;
  label: string;
  emoji?: string;
}

interface CategoryGroup {
  title: string;
  categories: Category[];
}

const categoryGroups: CategoryGroup[] = [
  {
    title: "🎨 Creative & Visual",
    categories: [
      { value: "ai-art", label: "AI Art", emoji: "🎨" },
      { value: "anime", label: "Anime", emoji: "🎌" },
      { value: "3d-renders", label: "3D Renders", emoji: "🎲" },
      { value: "character-design", label: "Character Design", emoji: "👤" },
      { value: "image-editing", label: "Image Editing", emoji: "✂️" },
      { value: "concept-art", label: "Concept Art", emoji: "🖼️" },
      { value: "ai-edits", label: "AI Edits", emoji: "✨" },
      { value: "aesthetic-photography", label: "Aesthetic Photography", emoji: "📸" },
    ],
  },
  {
    title: "💡 Writing & Storytelling",
    categories: [
      { value: "story-scriptwriting", label: "Story & Scriptwriting", emoji: "📖" },
      { value: "poetry", label: "Poetry", emoji: "✍️" },
      { value: "blogging", label: "Blogging", emoji: "📝" },
      { value: "copywriting", label: "Copywriting", emoji: "✏️" },
      { value: "marketing-content", label: "Marketing Content", emoji: "📢" },
      { value: "social-media-posts", label: "Social Media Posts", emoji: "📱" },
      { value: "trendy-ai-posts", label: "Trendy AI Posts", emoji: "🔥" },
      { value: "dialogue-prompts", label: "Dialogue Prompts", emoji: "💬" },
    ],
  },
  {
    title: "🧠 AI & Tech",
    categories: [
      { value: "stable-diffusion", label: "Stable Diffusion", emoji: "🌊" },
      { value: "midjourney", label: "Midjourney", emoji: "🚀" },
      { value: "chatgpt", label: "ChatGPT", emoji: "🤖" },
      { value: "gemini", label: "Gemini", emoji: "♊" },
      { value: "claude", label: "Claude", emoji: "🧠" },
      { value: "ai-ml-finetuning", label: "AI/ML Fine-Tuning", emoji: "⚙️" },
      { value: "prompt-engineering", label: "Prompt Engineering", emoji: "🔧" },
      { value: "api-automation", label: "API & Automation", emoji: "🔗" },
    ],
  },
  {
    title: "💼 Professional & Business",
    categories: [
      { value: "hr-recruitment", label: "HR & Recruitment", emoji: "👔" },
      { value: "management-operations", label: "Management & Operations", emoji: "📊" },
      { value: "product-management", label: "Product Management", emoji: "📦" },
      { value: "design-thinking", label: "Design Thinking", emoji: "💡" },
      { value: "business-strategy", label: "Business Strategy", emoji: "📈" },
      { value: "branding-identity", label: "Branding & Identity", emoji: "🎯" },
      { value: "productivity-prompts", label: "Productivity Prompts", emoji: "⚡" },
    ],
  },
  {
    title: "📚 Education & Research",
    categories: [
      { value: "study-aids", label: "Study Aids", emoji: "📚" },
      { value: "data-science-engineering", label: "Data Science & Engineering", emoji: "🔬" },
      { value: "coding-development", label: "Coding & Development", emoji: "💻" },
      { value: "research-assistance", label: "Research Assistance", emoji: "🔍" },
      { value: "ai-learning-resources", label: "AI Learning Resources", emoji: "🎓" },
      { value: "educational-games", label: "Educational Games", emoji: "🎮" },
    ],
  },
  {
    title: "🎮 Entertainment & Media",
    categories: [
      { value: "gaming", label: "Gaming", emoji: "🎮" },
      { value: "movie-concepts", label: "Movie Concepts", emoji: "🎬" },
      { value: "meme-creation", label: "Meme Creation", emoji: "😂" },
      { value: "animation-ideas", label: "Animation Ideas", emoji: "🎞️" },
      { value: "music-prompts", label: "Music Prompts", emoji: "🎵" },
    ],
  },
  {
    title: "🌐 Others / Daily Life",
    categories: [
      { value: "personal-development", label: "Personal Development", emoji: "🌱" },
      { value: "motivation", label: "Motivation", emoji: "💪" },
      { value: "mental-health", label: "Mental Health", emoji: "🧘" },
      { value: "relationship-prompts", label: "Relationship Prompts", emoji: "❤️" },
      { value: "travel-lifestyle", label: "Travel & Lifestyle", emoji: "✈️" },
      { value: "food-recipes", label: "Food & Recipes", emoji: "🍳" },
      { value: "general-chat", label: "General Chat", emoji: "💭" },
    ],
  },
];

const allCategories = categoryGroups.flatMap(group => group.categories);

interface CategoryComboboxProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function CategoryCombobox({ value, onChange, className }: CategoryComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [customCategory, setCustomCategory] = React.useState("");
  const [showCustomInput, setShowCustomInput] = React.useState(false);

  const selectedCategory = allCategories.find(cat => cat.value === value);
  const isOther = value && !selectedCategory;

  React.useEffect(() => {
    if (isOther) {
      setShowCustomInput(true);
      setCustomCategory(value);
    }
  }, [value, isOther]);

  const handleSelect = (currentValue: string) => {
    if (currentValue === "other") {
      setShowCustomInput(true);
      setCustomCategory("");
      onChange("");
    } else {
      setShowCustomInput(false);
      onChange(currentValue === value ? "" : currentValue);
      setOpen(false);
    }
  };

  const handleCustomCategoryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setCustomCategory(newValue);
    onChange(newValue);
  };

  return (
    <div className={cn("space-y-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between bg-background/50 border-border/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all",
              !value && "text-muted-foreground"
            )}
          >
            {selectedCategory ? (
              <span>
                {selectedCategory.emoji} {selectedCategory.label}
              </span>
            ) : isOther ? (
              <span>✏️ {customCategory || "Other (Custom)"}</span>
            ) : (
              "Select a category or type to search..."
            )}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 max-h-[400px]" align="start">
          <Command>
            <CommandInput placeholder="Search categories..." className="h-9" />
            <CommandEmpty>No category found.</CommandEmpty>
            <CommandList className="max-h-[300px] overflow-y-auto">
              {categoryGroups.map((group) => (
                <CommandGroup key={group.title} heading={group.title}>
                  {group.categories.map((category) => (
                    <CommandItem
                      key={category.value}
                      value={category.value}
                      onSelect={() => handleSelect(category.value)}
                      keywords={[category.label]}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          value === category.value ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <span>{category.emoji} {category.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
              <CommandGroup heading="Custom">
                <CommandItem
                  value="other"
                  onSelect={() => handleSelect("other")}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      isOther ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span>✏️ Other (Custom Category)</span>
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showCustomInput && (
        <div className="space-y-1">
          <Input
            placeholder="Enter your custom category name..."
            value={customCategory}
            onChange={handleCustomCategoryChange}
            className="bg-muted/30 border-border/30 focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all rounded-xl"
          />
          <p className="text-xs text-muted-foreground">
            Enter a unique category name for your prompt
          </p>
        </div>
      )}
    </div>
  );
}
